import CASBlock from "@components/Blocks/CASBlock/CASBlock";
import Advertisement from "@components/Elements/Advertisement/Advertisement";
import { GoogleAdsSlotFactory } from "@components/Elements/Advertisement/helpers";
import { pubPageAdTargeting } from "@config/ads";
import { CAS_UGC_SECTION_BANNER_ID } from "@helper/getEnvVariables";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import { fetchEnvelope, useLiveRamp } from "@hooks/useLiveRamp";
import type { BHBasePageProps } from "@routes/BHBasePage";
import BHBasePage from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import WidgetWrapper from "@routes/Home/HomePageLayout/WidgetWrapper";
import NotFound from "@routes/NotFound";
import styles from "@routes/UGC/UGCLandingPage/UGCLandingPage.module.css";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import {
  AdTargetingType,
  AdvertisementTypeEnum,
  PageAdTargetingTypeEnum,
  PageAdTargetValue,
} from "@typings/Ads.d";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import fetchUGCLandingPageData, {
  UGCLandingPayloadResponseType,
} from "./UGCLandingPage.server";
import UGCLandingPageMetaData from "./UGCLandingPageMetaData";
import UGCStoriesListing from "./UGCStoriesListing";

export const MATA_MATA_SECTION = "mata-mata";

export function meta() {
  return [getMetaData(UGCLandingPageMetaData().metaTags)];
}

export default function UGCLandingPage() {
  useLiveRamp({ fetchEnvelope });
  const dataLoaderResponse: TRouteWithoutRedirect<
    UGCLandingPayloadResponseType,
    string
  > = useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const { data }: UGCLandingPayloadResponseType = dataLoaderResponse.payload;
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const basePageProps: BHBasePageProps = {
    uniqueSectionName: MATA_MATA_SECTION,
    hasLb1Ads: true,
    // gaData: gaData,
    // metaTags: metaTags,
    pageAdTargetValue: pageAdTargetValue,
  };

  const listingPageAdTargeting = pubPageAdTargeting[
    pageAdTargetValue
  ] as AdTargetingType<string, PageAdTargetValue>;

  return (
    <BHBasePage {...basePageProps}>
      <WidgetWrapper>
        <h3 className={styles.sectionName}>{MATA_MATA_SECTION}</h3>
        <CASBlock
          className="min-h-[530px] lg:min-h-[495px]"
          casId={CAS_UGC_SECTION_BANNER_ID}
        />
        <div className={styles.ugcContentWrapper}>
          <div className={styles.ugcCampaignLists}>
            <UGCStoriesListing
              data={data}
              pageAdTargetValue={pageAdTargetValue}
            />
          </div>
          <div className={styles.ugcAdsSideBar}>
            <Advertisement
              adUnitProps={{
                type: AdvertisementTypeEnum.IMU2,
                slot: GoogleAdsSlotFactory.imu2(MATA_MATA_SECTION),
                pubAdTargeting: [listingPageAdTargeting],
              }}
            />
          </div>
        </div>
      </WidgetWrapper>
    </BHBasePage>
  );
}

export async function loader() {
  const data = await fetchUGCLandingPageData();
  const { gaData } = UGCLandingPageMetaData();

  return {
    ...data,
    gtm: getGTMData(gaData),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<UGCLandingPayloadResponseType | null, string>
  >;
}
